import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Content,
  Form,
  FormAlert,
  FormGroup,
  FormHelperText,
  Grid,
  GridItem,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextInput,
} from '@patternfly/react-core';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useSaveAccountingCustomers } from '../../../hooks/useAdminAccountingCustomers';
import type { AccountingEntriesCustomer } from '../../../types/models/Reconciliation';
import {
  buildCustomerCodesPatch,
  customerCodeError,
  customerCodesDraftFrom,
  isCustomerCodesDraftValid,
} from './customerCodesForm';
import type { CustomerCodesDraft } from './customerCodesForm';

interface WinAuditorCustomerCodesModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The entries view's `customers[]`: the customers the month's ODs name, missing codes first. */
  customers: AccountingEntriesCustomer[];
  /** Called after a successful save: the OD lines carry these codes, so the entries must be re-read. */
  onSaved: () => void;
}

const customerName = (c: AccountingEntriesCustomer) =>
  c.name ?? (c.accounting_key !== null ? `Client Facturier n° ${c.accounting_key}` : 'Client sans nom');

/**
 * « Codes clients WinAuditor » — the WinAuditor customer code (`C` + code) of each customer the month's ODs
 * name (`PATCH /admin/accounting/customers`). WinAuditor assigns these codes itself; Sophie copies them here
 * so the ODs can be exported. Only the changed codes are sent (the endpoint merges, all-or-nothing); after a
 * save the entries reload. No `elementToFocus` (see AccountingAccountsModal).
 */
const WinAuditorCustomerCodesModal: React.FunctionComponent<WinAuditorCustomerCodesModalProps> = ({
  isOpen,
  onClose,
  customers,
  onSaved,
}) => {
  const { save, saving } = useSaveAccountingCustomers();
  const { addNotification, setDrawerOpen } = useNotifications();
  const [draft, setDraft] = useState<CustomerCodesDraft>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const seededRef = useRef(false);

  // Seed the draft once per open — never while the user is editing (a refetch of the entries behind the
  // modal must not wipe unsaved codes).
  useEffect(() => {
    if (!isOpen) {
      seededRef.current = false;
      return;
    }
    if (!seededRef.current) {
      seededRef.current = true;
      setDraft(customerCodesDraftFrom(customers));
      setFormError(null);
      setServerErrors({});
    }
  }, [isOpen, customers]);

  const patch = useMemo(() => buildCustomerCodesPatch(customers, draft), [customers, draft]);
  const canSave = !saving && patch !== null && isCustomerCodesDraftValid(draft);

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  const submit = async () => {
    if (!canSave || patch === null) {
      return;
    }
    setFormError(null);
    setServerErrors({});
    const result = await save(patch);
    if (result.ok) {
      const n = Object.keys(patch.customers).length;
      addNotification({
        title: 'Codes clients WinAuditor enregistrés',
        srTitle: 'Success alert',
        description: `${n} ${n === 1 ? 'code modifié' : 'codes modifiés'}. Les écritures du mois sont relues.`,
        variant: 'success',
      });
      setDrawerOpen(true);
      onSaved();
      onClose();
      return;
    }
    setFormError(result.message);
    setServerErrors(result.fieldErrors);
    addNotification({
      title: "Impossible d'enregistrer les codes clients WinAuditor",
      srTitle: 'Error alert',
      description: result.message,
      variant: 'danger',
    });
    setDrawerOpen(true);
  };

  const generalErrors = Object.entries(serverErrors).filter(([key]) => !key.startsWith('customers.'));

  return (
    <Modal
      variant="medium"
      isOpen={isOpen}
      onClose={handleClose}
      aria-labelledby="winauditor-customer-codes-title"
      aria-describedby="winauditor-customer-codes-description"
    >
      <ModalHeader
        title="Codes clients WinAuditor"
        labelId="winauditor-customer-codes-title"
        description="Le code que WinAuditor a donné à chaque client des opérations diverses du mois (par exemple 0000004, zéros compris). Une OD ne s'exporte que lorsque tous ses clients ont leur code."
        descriptorId="winauditor-customer-codes-description"
      />
      <ModalBody tabIndex={0} aria-label="Codes clients WinAuditor du mois">
        {customers.length === 0 ? (
          <Content component="p">Aucune opération diverse de ce mois ne nomme de client.</Content>
        ) : (
          <Form
            id="winauditor-customer-codes-form"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            {formError && (
              <FormAlert>
                <Alert variant="danger" isInline isLiveRegion title={formError}>
                  {generalErrors.map(([key, message]) => (
                    <div key={key}>{message}</div>
                  ))}
                </Alert>
              </FormAlert>
            )}
            <Grid hasGutter>
              {customers.map((c) => {
                const name = customerName(c);
                const details = [
                  c.vat_number,
                  c.accounting_key !== null ? `Client Facturier n° ${c.accounting_key}` : null,
                ]
                  .filter(Boolean)
                  .join(' · ');
                if (c.accounting_key === null) {
                  return (
                    <GridItem key={`client-${c.client_id ?? name}`}>
                      <Content component="p">
                        <strong>{name}</strong>
                        {details && <> · {details}</>}
                        <br />
                        <span className="pf-v6-u-font-size-sm pf-v6-u-text-color-subtle">
                          Pas encore de client Facturier : aucun code ne peut être enregistré.
                        </span>
                      </Content>
                    </GridItem>
                  );
                }
                const key = String(c.accounting_key);
                const id = `winauditor-customer-${key}`;
                const value = draft[key] ?? '';
                const helper = serverErrors[`customers.${key}`] ?? customerCodeError(value);
                const isMissing = value.trim() === '';
                return (
                  <GridItem key={key}>
                    <FormGroup label={name} fieldId={id}>
                      <Grid hasGutter>
                        <GridItem md={5}>
                          <TextInput
                            id={id}
                            value={value}
                            onChange={(_e, v) => setDraft((prev) => ({ ...prev, [key]: v }))}
                            validated={helper ? 'error' : 'default'}
                            aria-describedby={`${id}-helper`}
                            isDisabled={saving}
                          />
                        </GridItem>
                      </Grid>
                      <FormHelperText>
                        <HelperText id={`${id}-helper`}>
                          <HelperTextItem variant={helper ? 'error' : isMissing ? 'warning' : 'default'}>
                            {helper ?? [details, isMissing ? 'code manquant' : null].filter(Boolean).join(' · ')}
                          </HelperTextItem>
                        </HelperText>
                      </FormHelperText>
                    </FormGroup>
                  </GridItem>
                );
              })}
            </Grid>
          </Form>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          variant="primary"
          type="submit"
          form="winauditor-customer-codes-form"
          isLoading={saving}
          isDisabled={!canSave}
        >
          Enregistrer
        </Button>
        <Button variant="link" onClick={handleClose} isDisabled={saving}>
          Annuler
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default WinAuditorCustomerCodesModal;
